import * as ko from 'knockout';
import * as localisationHelper from 'localisationHelper';

// Models
import { JoinConfiguration } from 'Content/ts/Models/Join/JoinConfiguration';
import { PriceBreakdownDetailsModel } from 'Content/ts//Models/Shared/PriceBreakdown/PriceBreakdownDetailsModel';
import { JoiningStepEnum } from 'Content/ts/Models/Join/JoiningStep';
import { SelectDropdownOptions } from 'Content/ts/Models/Controls/inputSelectOptions';
import { IMarketingSourceModel } from 'Content/ts/Models/CommunicationPreferences/MarketingSourceModel';
import { PaymentProviderTypes } from 'Content/ts/Enums/UniversalBasket/paymentProviderTypes';

// Services
import { MarketingSourcesService } from 'Content/ts/CommunicationPreferences/MarketingSources/Services/MarketingSourcesService';
import { ConfigurableTextService } from 'Scripts/ts/Localisation/ConfigurableTextService';
import { JoiningService } from 'Content/ts/Joining/Services/JoiningService';
import { ValidationService } from 'Content/ts/Shared/Services/Validation/validationService';

// Components
import 'Content/ts/Shared/BillingDetails/billing-details-capture';
import 'Content/ts/Shared/Buttons/StandardButtons/Inverse/button-inverse';
import 'Content/ts/Shared/DirectDebitDetails/direct-debit-details';
import 'Content/ts/Shared/PriceBreakdown/price-breakdown';
import 'Content/ts/Join/deals-and-news';
import 'Content/ts/Join/payment-details';
import "Content/ts/Shared/Inputs/Checkbox/input-checkbox-observable";

interface PaymentParams {
    JoinConfig: JoinConfiguration;
}

export class Payment {
    customMessages: {
        DirectDebitDateText: string;
        Of: string;
        MarketingSourceQuestion: string;
        ReferrerPromptText: string;
        InvalidMemberNumber: string;
        UsePreviouslyEnteredDetails: string;
    };

    configuration: JoinConfiguration;
    priceDetails: PriceBreakdownDetailsModel;

    showCollectionDateComponent: KnockoutObservable<boolean>;
    loadingPriceDetails: KnockoutObservable<boolean>;
    defaultCollectionDay: KnockoutComputed<number>;

    showDirectDebit: KnockoutComputed<boolean>;
    showBillingDetails: KnockoutComputed<boolean>;
    directDebitDateText: KnockoutComputed<string>;
    isWPM: KnockoutComputed<boolean>;

    marketingSources: KnockoutObservableArray<SelectDropdownOptions>;
    marketingSourceId: KnockoutObservable<number>;
    referringMember: KnockoutObservable<string>;
    contactFieldsLoaded: KnockoutObservable<boolean>;
    autoPopulateBillingDetails: KnockoutObservable<boolean>;

    validationFields: KnockoutObservable<any>[];
    showErrorMessage: KnockoutObservable<boolean>;
    subscriptions: KnockoutSubscription[];

    constructor(params: PaymentParams) {
        this.configuration = params.JoinConfig;
        this.contactFieldsLoaded = params.JoinConfig.contactFieldsLoaded;
        this.loadingPriceDetails = params.JoinConfig.priceDetailsLoading;
        this.marketingSourceId = params.JoinConfig.contactDetails.MarketingSourceId;

        this.showCollectionDateComponent = ko.observable(true);

        this.priceDetails = this.configuration.priceDetails;
        this.marketingSources = ko.observableArray([]);
        this.referringMember = this.configuration.contactDetails.ReferringMember;
        this.validationFields = this.configuration.getValidationFields(JoiningStepEnum.Payment);
        this.showErrorMessage = ko.observable(false);
        this.autoPopulateBillingDetails = ko.observable(false).extend({ uid: 'auto-populate-billing-details' });
        this.subscriptions = [];

        this.setupComputeds();
        this.retrieveCustomMessages();
        this.setupSubscribes();
        this.additionalValidation();
        this.fetchMarketingSources();

    }

    private fetchMarketingSources(): void {
        MarketingSourcesService.getMarketingSources(true)
            .then((response: IMarketingSourceModel[]) => {
                this.marketingSources(response.map(ms => {
                    return {
                        Id: ms.Id,
                        Text: ms.ShortDescription
                    } as SelectDropdownOptions
                }));
            });
    }

    private retrieveCustomMessages(): void {
        this.customMessages = {
            DirectDebitDateText: localisationHelper.getString("GenericResources", "DD_FirstCollectionDate"),
            Of: localisationHelper.getString("GenericResources", "Of"),
            MarketingSourceQuestion: ConfigurableTextService.RetrieveTextSync("JourneyMarketingSourceQuestion"),
            ReferrerPromptText: ConfigurableTextService.RetrieveTextSync("JourneyReferrerText"),
            InvalidMemberNumber: localisationHelper.getString("JoinResources", "InvalidMemberNumber"),
            UsePreviouslyEnteredDetails: localisationHelper.getString("JoinResources", "UsePreviouslyEnteredDetails"),
        };
    }

    private additionalValidation(): void {
        ValidationService.AddValidationRule(this.referringMember, this.validationFields, {
            validation: {
                validator: () => {
                    if (!this.showErrorMessage()) {
                        return true;
                    }
                    else
                        return false;
                },
                message: this.customMessages.InvalidMemberNumber
            }
        });
    }

    private setupComputeds(): void {
        this.showDirectDebit = ko.pureComputed(() => {
            return this.priceDetails.CycleFee() !== null && this.priceDetails.IsDirectDebit();
        });

        this.showBillingDetails = ko.pureComputed((): boolean => {
            return this.configuration.paymentProvider() === PaymentProviderTypes.VerifoneSCA;
        });

        this.defaultCollectionDay = ko.pureComputed(() => {
            return this.priceDetails.HasFees()
                ? this.priceDetails.MCD()
                : null;
        });

        this.directDebitDateText = ko.pureComputed(() => {
            return this.priceDetails.HasFees() && this.priceDetails.FirstBillDate() !== null
                ? this.customMessages.DirectDebitDateText
                    .replace("{0}", `${this.priceDetails.FirstBillDate().format("Do")} ${this.customMessages.Of} ${this.priceDetails.FirstBillDate().format("MMMM YYYY")}`)
                : "";
        });

        this.isWPM = ko.pureComputed(() => {
            return this.configuration.paymentProvider() === PaymentProviderTypes.WPM;
        });
    }

    private setupSubscribes(): void {
        this.subscriptions.push(
            this.referringMember.subscribe(val => {
                if (val) {
                    JoiningService.validateMemberNumber(val)
                        .then((result) => {
                            this.showErrorMessage(!result.IsValid);
                        });
                }
                else {
                    this.showErrorMessage(false);
                }
            }));

        this.subscriptions.push(
            this.autoPopulateBillingDetails.subscribe(val => {
                if (val) {
                    this.configuration.billingDetailsPopulateFromContactDetails();
                }
                else {
                    this.configuration.billingDetailsClear();
                }
            }));            
    }

    public dispose() {
        this.subscriptions.forEach((sub: KnockoutSubscription) => sub.dispose());
    }

}

const componentName = 'payment';
ko.components.unregister(componentName);
ko.components.register(componentName, {
    viewModel: Payment,
    template: { require: `text!/Themes/Default/Content/ts/Join/${componentName}.html` }
});